import { Link } from "wouter";
import { Crown, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMyTier } from "@/hooks/use-my-tier";

const TIER_STYLES: Record<string, { label: string; className: string; icon: "crown" | "sparkles" | null }> = {
  lite: {
    label: "Lite",
    className: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600",
    icon: null,
  },
  pro: {
    label: "Pro",
    className: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700",
    icon: "sparkles",
  },
  business: {
    label: "Business",
    className: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-700",
    icon: "sparkles",
  },
  enterprise: {
    label: "Enterprise",
    className: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700",
    icon: "crown",
  },
};

interface PlanBadgeProps {
  isAdmin: boolean;
}

export function PlanBadge({ isAdmin }: PlanBadgeProps) {
  const { data, isLoading } = useMyTier();
  const tier = data?.tier;
  const displayName = data?.displayName;

  if (isLoading || !tier) return null;

  const style = TIER_STYLES[tier] || TIER_STYLES.lite;
  const Icon = style.icon === "crown" ? Crown : style.icon === "sparkles" ? Sparkles : null;
  // Hide upgrade CTA when already on the highest tier
  const isTopTier = tier === "enterprise";

  return (
    <div className="flex items-center gap-2">
      <Badge
        variant="outline"
        className={`hidden sm:flex items-center gap-1 px-2 py-0.5 text-xs font-semibold ${style.className}`}
        data-testid={`badge-plan-${tier}`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {displayName || style.label}
      </Badge>
      {isAdmin && !isTopTier && (
        <Link href="/upgrade">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs hidden md:flex"
            data-testid="button-upgrade-plan"
          >
            Upgrade
          </Button>
        </Link>
      )}
    </div>
  );
}
